import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogConfig } from '@angular/material/dialog';

import { DialogClass, IDialogConfig } from './_models';

@Injectable({
	providedIn: 'root'
})
export class DialogService {
	public get dialogs(): MatDialogRef<any>[] {
		return this.dialog.openDialogs;
	}

	constructor(public dialog: MatDialog) {}

	public open(dialogTemplate: any, config: IDialogConfig, onClose?: () => {}): string {
		config.data = config.data || {};
		config.panelClass = config.panelClass || DialogClass.medium;
		config.disableClose = config.disableClose === true;
		if (config.height) {
			config.maxHeight = '90vh';
		}

		const dialogRef = this.dialog.open(dialogTemplate, config);
		dialogRef.afterClosed().subscribe((ignore: boolean) => {
			if (onClose && !ignore) {
				onClose();
			}
		});
		this.dialogs.push(dialogRef);

		return dialogRef.id;
	}

	public close(id: string) {
		const dialog = this.dialog.getDialogById(id);
		if (dialog && dialog.disableClose !== true) {
			dialog.close(false);
		}
	}

	public closeAll(force: boolean) {
		if (force) {
			this.dialog.closeAll();
		} else {
			this.dialogs.forEach((d: MatDialogRef<any>) => {
				if (d.disableClose !== true) {
					d.close(true);
				}
			});
		}
	}

	/**
	 * Utility method that will override the default dialog close function and returns a reset and close action. This
	 * is useful because it allows us to intercept the close action, run whatever custom functionality we need and
	 * then manually close and restore the dialog action.
	 *
	 * Usage:
	 *
	 * Call the function and provide the MatDialogRef, the calling components context (this), your custom close action and
	 * store the return function.
	 * this.dialogRefClose = this.dialogService.interceptClose(this.dialogRef, this, this.closeDialog)
	 *
	 * When closing the dialog just call:
	 * this.dialogRefClose();
	 *
	 * This will restore the original MatDialogRef close function and call it which closes the dialog.
	 *
	 * @param dialogRef The MatDialogRef of the dialog we want to intercept the close call for
	 * @param context The calling contexts "this"
	 * @param closeDialog Custom close function to call instead of MatDialogRef.close
	 */
	public interceptClose(dialogRef: MatDialogRef<any>, context: any, closeDialog: () => void | any): () => void {
		const close = dialogRef.close;

		dialogRef.close = closeDialog.bind(context);

		return () => {
			dialogRef.close = close.bind(dialogRef);
			dialogRef.close();
		};
	}
}
